/**
 * GameCard — карточка игры для витрины и каталога.
 *
 * Анатомия из дизайн-системы (раздел 09). Что здесь важно и почему:
 *
 *  • Обложка 16/9 жёстко — ряд карточек выравнивается по низу без JS.
 *  • Нет обложки → детерминированный градиент из id + инициалы. Серый
 *    прямоугольник читается как «сломано», а цвет по id даёт узнаваемость:
 *    при перезагрузке игра выглядит так же.
 *  • Метки не копятся: раньше на обложку могло лечь до пяти штук
 *    (статус, ХИТ, мультиплеер, возраст, запуски) — они перекрывали
 *    саму картинку, ради которой карточка и существует. Теперь статус
 *    и «Хит» взаимоисключающи, мультиплеер уехал в подвал.
 *  • Вся карточка — одна кнопка. Кнопка «Играть» внутри была бы лишним
 *    прицелом для мыши: попасть надо в карточку, а не в 80 пикселей.
 *
 * Описания и даты в карточке нет намеренно: в каталоге их не читают,
 * а место они отнимают у обложки, которая игру и продаёт.
 */
import React from "react";
import CabIcon from "../cabinet/CabIcon";
import { Pill, fmtCompact } from "../ui";
import { genreLabel, statusMeta } from "../../lib/gameMeta";
import "../../styles/game-card.css";

/* Палитра фолбэк-обложек. Не случайная: выбирается по id, поэтому
   одна и та же игра всегда одного цвета. Оттенки взяты насыщенные —
   на них белые инициалы читаются в любой теме. */
const COVER_COLORS = [
  ["#3357ff", "#5b7bff"],
  ["#12b76a", "#0ba5ec"],
  ["#f79009", "#f04438"],
  ["#8b5cf6", "#ec4899"],
  ["#06b6d4", "#3357ff"],
  ["#ef4444", "#f59e0b"],
  ["#10b981", "#84cc16"],
  ["#6366f1", "#a855f7"],
];

function coverGradient(id, title) {
  // id может отсутствовать у черновика — тогда считаем по названию
  const seed = id != null ? Number(id) : String(title || "?").length;
  const [a, b] = COVER_COLORS[Math.abs(seed) % COVER_COLORS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

/** Инициалы: до двух букв из первых двух слов — «Ночные гонки» → «НГ». */
function initials(title) {
  const words = String(title || "?").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default function GameCard({ game, onClick, showStatus = false }) {
  if (!game) return null;

  const {
    id,
    title,
    author_username: author,
    thumbnail,
    play_count: plays = 0,
    likes_count: likes = 0,
    multiplayer,
    max_players: maxPlayers,
    rating_percent: rating = 0,
    age_rating: age,
    genre,
  } = game;

  // Пустая строка в thumbnail — реальный случай (например id=869):
  // без проверки React рендерит <img src=""> и ломает раскладку.
  const hasThumb = typeof thumbnail === "string" && thumbnail.length > 0;
  const status = showStatus ? statusMeta(game) : null;
  const isHit = rating >= 90;

  return (
    <button
      type="button"
      className="rb-gcard"
      onClick={() => onClick && onClick(id)}
      title={title}
    >
      <div
        className="rb-gcard__cover"
        style={hasThumb ? undefined : { background: coverGradient(id, title) }}
      >
        {hasThumb ? (
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            decoding="async"
            className="rb-gcard__img"
          />
        ) : (
          <div className="rb-gcard__noimg">{initials(title)}</div>
        )}

        {/* Статус и «Хит» взаимоисключающи: игра на модерации не может
            одновременно быть хитом недели, а две метки в углу спорят. */}
        <div className="rb-gcard__badges">
          {status ? (
            <Pill tone={status.tone}>{status.label}</Pill>
          ) : (
            isHit && <Pill tone="accent">Хит</Pill>
          )}
        </div>

        {age ? <span className="rb-gcard__age">{age}+</span> : null}

        {/* Лайки — на обложке, потому что это единственная оценка от живых
            игроков, а не счётчик. Живого онлайна здесь нет намеренно:
            /feed его не отдаёт (проверено на боевом API — в ответе поля
            online_count нет), а рисовать плашку, которая никогда не
            заполнится, — мёртвый код. */}
        {likes > 0 && (
          <span className="rb-gcard__live">
            <CabIcon name="heart" size={12} />
            {fmtCompact(likes)}
          </span>
        )}
      </div>

      <div className="rb-gcard__body">
        <span className="rb-gcard__title">{title || "Без названия"}</span>
        {author && <span className="rb-gcard__author">{author}</span>}

        <span className="rb-gcard__foot">
          {genre ? (
            <Pill tone="soft" className="rb-gcard__genre">
              {genreLabel(genre)}
            </Pill>
          ) : (
            <span />
          )}
          <span className="rb-gcard__plays">
            {multiplayer && (
              <CabIcon
                name="users"
                size={12}
                title={maxPlayers ? `До ${maxPlayers} игроков` : "Мультиплеер"}
              />
            )}{" "}
            <b>{fmtCompact(plays)}</b> запусков
          </span>
        </span>
      </div>
    </button>
  );
}

/**
 * Скелетон повторяет анатомию карточки, а не рисует серый прямоугольник:
 * обложка 16/9, две строки заголовка, строка автора, подвал. Когда данные
 * приедут, ничего не сдвинется.
 */
export function GameCardSkeleton() {
  return (
    <div className="rb-gcard rb-gcard--skeleton" aria-hidden="true">
      <div className="rb-gcard__cover">
        <span
          className="ui-skeleton"
          style={{ display: "block", width: "100%", height: "100%", borderRadius: 0 }}
        />
      </div>
      <div className="rb-gcard__body">
        <span className="ui-skeleton" style={{ display: "block", height: 15, width: "88%" }} />
        <span className="ui-skeleton" style={{ display: "block", height: 12, width: "52%" }} />
        <span
          className="ui-skeleton"
          style={{ display: "block", height: 12, width: "70%", marginTop: "auto" }}
        />
      </div>
    </div>
  );
}
